import React, { useRef, useCallback, useMemo, useEffect } from 'react'

import Scrollbars from '../../Scrollbars'

import { BasicOption } from './interfaces'

export const useOptionsList = <T extends BasicOption>({
  options,
  focusedId,
  disableAutoScroll = false,
  externalListRef,
}: {
  options: T[]
  focusedId?: string
  disableAutoScroll?: boolean
  externalListRef?: React.RefObject<HTMLUListElement>
}) => {
  const internalListRef = useRef<HTMLUListElement | null>(null)
  const listRef = externalListRef || internalListRef
  const scrollerRef = useRef<Scrollbars | null>(null)

  const focusableOptionIds = useMemo(() => {
    return options.reduce((acc, option) => {
      if (option.type === 'group') return acc
      if (!option.id) return acc
      return [...acc, option.id]
    }, [] as string[])
  }, [options])

  const scrollItemInView = useCallback(
    (id: string) => {
      if (disableAutoScroll) return
      if (!scrollerRef.current?.view) return

      const item = listRef.current?.querySelector(`#${id}`) as HTMLLIElement
      if (!item) return

      const { top: itemTop, bottom: itemBottom } = item.getBoundingClientRect()
      const { top: scrollerTop, bottom: scrollerBottom } =
        scrollerRef.current.view.getBoundingClientRect()

      const isLastItem = id === focusableOptionIds[focusableOptionIds.length - 1]
      const isFirstItem = id === focusableOptionIds[0]

      /** scrolling down */
      if (itemBottom > scrollerBottom) {
        if (isLastItem) {
          scrollerRef.current.view.scrollTop = scrollerRef.current.view.scrollHeight
        } else {
          scrollerRef.current.view.scrollTop =
            item.offsetTop + item.offsetHeight - scrollerRef.current.view.clientHeight
        }
        /** scrolling up */
      } else if (itemTop < scrollerTop) {
        if (isFirstItem) {
          scrollerRef.current.view.scrollTop = 0
        } else {
          scrollerRef.current.view.scrollTop = item.offsetTop
        }
      }
    },
    [disableAutoScroll, focusableOptionIds, listRef],
  )

  useEffect(() => {
    focusedId && scrollItemInView(focusedId)
  }, [focusedId, scrollItemInView])

  return {
    listRef,
    scrollerRef,
  }
}
